import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	Table,
	TableBody,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material'
import * as React from 'react'
import CloseIcon from '@mui/icons-material/Close'

import PunchClockIcon from '@mui/icons-material/PunchClock'
import { Employee } from '../../../types/Employee/Employee'
import {
	confirmIndirectTimesheet,
	getIndirectTimesheet,
} from '../../../services/shiftEndReportService'
import {
	SubordinateTaskBreakdown,
	TaskBucket,
	TimeSheetResolutionStatus,
} from '../../../types/Reports/ShiftEnd/TimeSheet'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useProjectFeaturesContext } from '../../project/contexts/ProjectFeaturesContext'
import { StyledTableCell } from '../../core/StyledTableCell'
import { ContributorsDialog } from './ContributorsDialog'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'

type TimeSheetDialogProps = {
	employee: Employee
	postConfirmLoadData: () => void
	shiftDate: Date
}

dayjs.extend(utc)
dayjs.extend(timezone)

function formatHours(hours: number): string {
	return `${hours.toFixed(2)} h`
}

function punchClockColor(
	timesheetConfirmed: boolean | undefined,
	resolutionStatus: number | null | undefined,
): 'success' | 'warning' | 'error' {
	if (!timesheetConfirmed) return 'error'
	if (resolutionStatus === TimeSheetResolutionStatus.Pending) return 'warning'
	return 'success'
}

/** Path column — same rendering as ActivityRows for WorkerGrid */
function renderActivityPath(bucket: TaskBucket) {
	if (bucket.activity === 'Constraint') {
		return bucket.type
	}
	if (!bucket.type?.includes(' > ')) {
		return bucket.type && bucket.type !== bucket.activity ? bucket.type : null
	}
	const segments = bucket.type.split(' > ')
	return segments.map(
		(wi, index, arr) =>
			index !== arr.length - 1 && (
				<span key={index}>
					{index + 1}. {wi} <br />
				</span>
			),
	)
}

function bucketRowKey(bucket: TaskBucket): string {
	if (bucket.workItemTaskId) return `wit:${bucket.workItemTaskId}`
	if (bucket.reworkId) return `rew:${bucket.reworkId}`
	if (bucket.constraintId) return `con:${bucket.constraintId}`
	return `other:${bucket.activity}:${bucket.type}`
}

export const TimeSheetDialog = ({
	employee,
	postConfirmLoadData,
	shiftDate,
}: TimeSheetDialogProps) => {
	const { projectTimeZone } = useProjectFeaturesContext()
	const [open, setOpen] = React.useState(false)
	const [startTime, setStartTime] = React.useState<Date>()
	const [endTime, setEndTime] = React.useState<Date>()
	const [loading, setLoading] = React.useState<boolean>(false)
	const [startRequired, setStartRequired] = React.useState(false)
	const [endRequired, setEndRequired] = React.useState(false)
	const [attendance, setAttendance] = React.useState<{
		signInAt: Date | undefined
		signOutAt: Date | undefined
	}>()
	const [taskBreakdown, setTaskBreakdown] = React.useState<SubordinateTaskBreakdown | null>(null)
	const [resolutionStatus, setResolutionStatus] = React.useState<number | null | undefined>(
		employee.resolutionStatus,
	)
	const [timesheetConfirmed, setTimesheetConfirmed] = React.useState(employee.timesheetConfirmed)
	const [contributorsBucket, setContributorsBucket] = React.useState<TaskBucket | null>(null)
	const [contributorsOpen, setContributorsOpen] = React.useState(false)

	React.useEffect(() => {
		setResolutionStatus(employee.resolutionStatus)
		setTimesheetConfirmed(employee.timesheetConfirmed)
	}, [employee.resolutionStatus, employee.timesheetConfirmed])

	const formatTime = (date: string | Date | undefined) => {
		if (!date) return 'n/a'
		return dayjs
			.utc(date)
			.tz(projectTimeZone || 'UTC')
			.format('h:mm A')
	}

	const loadData = async () => {
		setLoading(true)
		try {
			const timeSheet = await getIndirectTimesheet(employee.id, shiftDate)

			if (timeSheet && timeSheet.activities && timeSheet.activities.length > 0) {
				setStartTime(timeSheet.activities[0].startTime)
				setEndTime(timeSheet.activities[timeSheet.activities.length - 1].endTime ?? undefined)
			} else {
				setStartTime(undefined)
				setEndTime(undefined)
			}
			setAttendance({ signInAt: timeSheet?.signInAt, signOutAt: timeSheet?.signOutAt })
			setTaskBreakdown(timeSheet?.subordinateTaskBreakdown ?? null)
			setResolutionStatus(timeSheet?.resolutionStatus ?? null)
			setTimesheetConfirmed(timeSheet?.confirmed ?? false)
		} finally {
			setLoading(false)
		}
	}

	const handleClickOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation()
		setOpen(true)
		setTaskBreakdown(null)
		loadData().catch(console.error)
	}
	const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation()
		setOpen(false)
	}

	const totalHoursDecimal = React.useMemo(() => {
		if (!startTime || !endTime) return null
		const start = dayjs.utc(startTime)
		let end = dayjs.utc(endTime)
		if (!start.isValid() || !end.isValid()) return null
		if (end.isBefore(start) || end.isSame(start)) {
			end = end.add(1, 'day')
		}
		const hours = end.diff(start, 'minute') / 60
		return hours > 0 ? hours : null
	}, [startTime, endTime])

	const calculateHoursDisplay = () => {
		if (totalHoursDecimal == null) return '—'
		return formatHours(totalHoursDecimal)
	}

	const handleTimeChange = (
		value: string,
		setValue: React.Dispatch<React.SetStateAction<Date | undefined>>,
	) => {
		const timeParts = value.split(':')
		const hour = parseInt(timeParts[0], 10)
		const minute = parseInt(timeParts[1], 10)
		const projectTZ = projectTimeZone ?? 'UTC'

		// Input hours are project-local; compose onto shiftDate, then store as UTC (same shape as API)
		const localDateTime = dayjs
			.tz(dayjs(shiftDate).format('YYYY-MM-DD'), projectTZ)
			.hour(hour)
			.minute(minute)
			.second(0)
			.millisecond(0)

		setValue(localDateTime.utc().format('YYYY-MM-DDTHH:mm:ss') as unknown as Date)
	}

	const formatTimeForDisplay = (date: Date | undefined) => {
		if (!date) return ''
		return dayjs
			.utc(date)
			.tz(projectTimeZone ?? 'UTC')
			.format('HH:mm')
	}

	async function handleSubmit(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
		if (!startTime || !endTime) {
			!startTime && setStartRequired(true)
			!endTime && setEndRequired(true)
			return
		}
		setLoading(true)
		await confirmIndirectTimesheet(employee.id, startTime, endTime, shiftDate)
		setLoading(false)
		setOpen(false)
		postConfirmLoadData()
	}

	const sortedBuckets = React.useMemo(() => {
		if (!taskBreakdown?.buckets?.length) return []
		return [...taskBreakdown.buckets].sort((a, b) => b.percentage - a.percentage)
	}, [taskBreakdown])

	const allocationRows = React.useMemo(() => {
		if (sortedBuckets.length === 0) return []

		const totalHundredths = totalHoursDecimal == null ? null : Math.round(totalHoursDecimal * 100)

		if (totalHundredths == null) {
			return sortedBuckets.map((bucket) => ({
				...bucket,
				allocatedHours: null as number | null,
			}))
		}

		const exact = sortedBuckets.map((bucket) => (totalHundredths * bucket.percentage) / 100)
		const floors = exact.map((value) => Math.floor(value))
		const remainder = totalHundredths - floors.reduce((sum, value) => sum + value, 0)
		const byFraction = exact
			.map((value, index) => ({ index, fraction: value - floors[index] }))
			.sort((a, b) => b.fraction - a.fraction)

		const hundredths = [...floors]
		for (let i = 0; i < remainder; i++) {
			hundredths[byFraction[i].index] += 1
		}

		return sortedBuckets.map((bucket, index) => ({
			...bucket,
			allocatedHours: hundredths[index] / 100,
		}))
	}, [sortedBuckets, totalHoursDecimal])

	const incompleteWarning = React.useMemo(() => {
		if (!taskBreakdown?.isIncomplete || taskBreakdown.missingWorkers.length === 0) {
			return null
		}
		const missingCount = taskBreakdown.missingWorkers.length
		const presentCount = taskBreakdown.presentCount || missingCount
		const supervisorName = `${employee.firstName} ${employee.lastName}`.trim()
		return `${missingCount} of ${presentCount} present direct report${
			presentCount === 1 ? '' : 's'
		} of ${supervisorName} do not have a confirmed timesheet. This timesheet will be saved but pending resolution until all present laborers have confirmed timesheets.`
	}, [taskBreakdown, employee.firstName, employee.lastName])

	const isResolved = resolutionStatus === TimeSheetResolutionStatus.Resolved
	const isPending = resolutionStatus === TimeSheetResolutionStatus.Pending
	const iconColor = punchClockColor(timesheetConfirmed, resolutionStatus)
	const iconTitle = !timesheetConfirmed
		? 'Time sheet — not submitted'
		: isPending
		? 'Time sheet — pending resolution'
		: isResolved
		? 'Time sheet — resolved'
		: 'Time sheet'

	return (
		<>
			<IconButton onClick={handleClickOpen} title={iconTitle}>
				<PunchClockIcon sx={{ fontSize: 28 }} color={iconColor} />
			</IconButton>

			<Dialog open={open} onClose={handleClose} fullWidth maxWidth='md'>
				<div
					onClick={(e: React.MouseEvent<HTMLDivElement>) => {
						e.stopPropagation()
					}}
				>
					<DialogTitle
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1.5,
							pr: 6,
							flexWrap: 'wrap',
						}}
					>
						<span>
							{employee.firstName} {employee.lastName}&apos;s Time Sheet
						</span>
						{isPending && (
							<Chip
								size='small'
								color='warning'
								label='Pending resolution'
								title='Waiting on crew timesheets before hours can be allocated to tasks'
							/>
						)}
						{isResolved && (
							<Chip
								size='small'
								color='success'
								label='Resolved'
								title='Hours have been allocated to tasks from crew timesheets'
							/>
						)}
					</DialogTitle>
					<IconButton
						onClick={handleClose}
						sx={(theme) => ({
							position: 'absolute',
							right: 8,
							top: 8,
							color: theme.palette.grey[500],
						})}
					>
						<CloseIcon />
					</IconButton>
					<DialogContent>
						{loading ? (
							<CircularProgress color='success' />
						) : (
							<Box sx={{ display: 'flex', flexDirection: 'column' }} gap={3}>
								{isPending && (
									<Alert severity='warning'>
										Timesheet is saved but pending resolution. Hours will be allocated to tasks once
										all present laborers have confirmed timesheets.
									</Alert>
								)}
								{isResolved && (
									<Alert severity='success'>
										Timesheet is resolved — hours have been allocated across crew tasks.
									</Alert>
								)}

								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										gap: '2rem',
									}}
								>
									<span>Sign In: {formatTime(attendance?.signInAt)}</span>
									<span>Sign Out: {formatTime(attendance?.signOutAt)}</span>
								</div>

								<FormControl fullWidth>
									<TextField
										onChange={(e) => handleTimeChange(e.target.value, setStartTime)}
										label='Start Time'
										type='time'
										InputLabelProps={{
											shrink: true,
										}}
										autoFocus
										error={startRequired}
										value={formatTimeForDisplay(startTime)}
										inputProps={{
											step: 300,
										}}
									/>
								</FormControl>
								<FormControl fullWidth>
									<TextField
										onChange={(e) => handleTimeChange(e.target.value, setEndTime)}
										label='End Time'
										type='time'
										InputLabelProps={{
											shrink: true,
										}}
										error={endRequired}
										value={formatTimeForDisplay(endTime)}
										inputProps={{
											step: 300,
										}}
									/>
								</FormControl>

								<Typography>Total Hours: {calculateHoursDisplay()}</Typography>

								{taskBreakdown && (
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
										{incompleteWarning && <Alert severity='warning'>{incompleteWarning}</Alert>}

										<Box>
											<Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
												{isResolved ? 'Allocated hours' : 'Suggested allocation'}
											</Typography>
											<Typography variant='body2' color='text.secondary'>
												{isResolved
													? 'Derived from confirmed crew timesheets and saved on this timesheet.'
													: 'Based on confirmed crew timesheets. Not saved as separate activities until the timesheet is resolved.'}
											</Typography>
										</Box>

										{sortedBuckets.length === 0 ? (
											<Typography variant='body2' color='text.secondary'>
												No confirmed crew time available for this date.
											</Typography>
										) : (
											<>
												{totalHoursDecimal == null && (
													<Typography variant='body2' color='text.secondary'>
														Enter start and end to see allocated hours.
													</Typography>
												)}
												<TableContainer
													sx={{
														maxHeight: 320,
														width: '100%',
														overflowX: 'auto',
														overflowY: 'auto',
													}}
												>
													<Table stickyHeader size='small'>
														<TableHead>
															<TableRow>
																<StyledTableCell align='left'>Title</StyledTableCell>
																<StyledTableCell align='left'>Activity</StyledTableCell>
																<StyledTableCell align='right'>Share</StyledTableCell>
																<StyledTableCell align='right'>Hours</StyledTableCell>
																<StyledTableCell align='center'>Contributors</StyledTableCell>
															</TableRow>
														</TableHead>
														<TableBody>
															{allocationRows.map((row) => (
																<TableRow key={bucketRowKey(row)} sx={{ background: '#fff' }}>
																	<StyledTableCell align='left'>{row.activity}</StyledTableCell>
																	<StyledTableCell align='left'>
																		{renderActivityPath(row)}
																	</StyledTableCell>
																	<StyledTableCell align='right'>{row.percentage}%</StyledTableCell>
																	<StyledTableCell align='right' sx={{ fontWeight: 600 }}>
																		{row.allocatedHours == null
																			? '—'
																			: formatHours(row.allocatedHours)}
																	</StyledTableCell>
																	<StyledTableCell align='center'>
																		<Button
																			size='small'
																			variant='text'
																			sx={{ color: 'primary.main' }}
																			startIcon={<PeopleOutlineIcon />}
																			onClick={(e) => {
																				e.stopPropagation()
																				setContributorsBucket(row)
																				setContributorsOpen(true)
																			}}
																		>
																			View
																		</Button>
																	</StyledTableCell>
																</TableRow>
															))}
														</TableBody>
														<TableFooter>
															<TableRow>
																<StyledTableCell colSpan={2} sx={{ fontWeight: 600 }}>
																	Total
																</StyledTableCell>
																<StyledTableCell align='right' sx={{ fontWeight: 600 }}>
																	100%
																</StyledTableCell>
																<StyledTableCell align='right' sx={{ fontWeight: 600 }}>
																	{totalHoursDecimal == null ? '—' : formatHours(totalHoursDecimal)}
																</StyledTableCell>
																<StyledTableCell />
															</TableRow>
														</TableFooter>
													</Table>
												</TableContainer>
											</>
										)}
									</Box>
								)}
							</Box>
						)}
					</DialogContent>
					<DialogActions sx={{ justifyContent: 'space-between' }}>
						<Button color='warning' variant='contained' onClick={handleClose}>
							Cancel
						</Button>
						<Button color='success' variant='contained' onClick={handleSubmit}>
							Submit
						</Button>
					</DialogActions>
				</div>
			</Dialog>

			<ContributorsDialog
				open={contributorsOpen}
				onClose={() => {
					setContributorsOpen(false)
					setContributorsBucket(null)
				}}
				employeeId={employee.id}
				employeeName={`${employee.firstName} ${employee.lastName}`.trim()}
				shiftDate={shiftDate}
				bucket={contributorsBucket}
			/>
		</>
	)
}
